import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useAuthInfo } from '@sweetauth/core'

import { insertAtBeginningOfPage } from '@/helpers/api'
import {
  type UploadFilesPresignedResult,
  createUploadFilesPresigned,
} from '@/helpers/uploadUtils'

import { markFilesIndexStartedInCache } from '@/hooks/files/cache/fileCacheSync'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { organizationFilesIndexStatusQueryKeys } from '@/hooks/organization/types'
import { boostOrganizationFilesIndexPolling } from '@/hooks/organization/useOrganizationFilesIndexStatus'
import { BATCH_SIZE } from '@/hooks/organization/useUploadOrganizationFiles'

import type { PaginatedResponse } from '@/types/Api'
import type { File as UploadedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

interface UploadOrganizationFilesArgs {
  orgId: UUID | ''
  batchSize?: number
  onProgress?: (progress: number) => void
  rejectOnPartialFailure?: boolean
}

interface UploadOrganizationFilesMutationArgs {
  files: File[]
  tag_ids: UUID[]
  folder_id?: UUID
  extract_entities: boolean
  unrestricted: boolean
}

interface OrganizationCompleteParams {
  tag_ids: UUID[]
  folder_id?: UUID
  extract_entities: boolean
  unrestricted: boolean
}

const uploadFilesPresigned =
  createUploadFilesPresigned<OrganizationCompleteParams>({
    basePath: '/api/v1/organizations',
    entityIdName: 'organization ID',
    buildBatchCompleteBody: (
      s3Keys,
      { tag_ids, folder_id, extract_entities, unrestricted },
    ) => ({
      uploads: s3Keys.map((s3Key) => ({ s3_key: s3Key })),
      tag_ids,
      folder_id,
      extract_entities,
      unrestricted,
    }),
    useRetry: true,
    progressAfterComplete: true,
  })

export const useUploadOrganizationFilesPresigned = ({
  orgId,
  batchSize = BATCH_SIZE,
  onProgress,
  rejectOnPartialFailure = false,
}: UploadOrganizationFilesArgs) => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation<
    UploadFilesPresignedResult,
    Error,
    UploadOrganizationFilesMutationArgs,
    PaginatedResponse<UploadedFile> | undefined
  >({
    mutationFn: ({
      tag_ids,
      folder_id,
      files,
      extract_entities,
      unrestricted,
    }) =>
      uploadFilesPresigned(
        orgId,
        files,
        accessToken,
        { tag_ids, folder_id, extract_entities, unrestricted },
        batchSize,
        onProgress,
        { rejectOnPartialFailure },
      ),
    onError: (err, _req, context) => {
      const key = organizationFilesQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
    },
    onMutate: async () => {
      const key = organizationFilesQueryKeys.organization({ orgId })
      const previous =
        queryClient.getQueryData<PaginatedResponse<UploadedFile>>(key)
      boostOrganizationFilesIndexPolling({ queryClient, orgId })
      return previous
    },
    onSuccess: (data) => {
      const key = organizationFilesQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<UploadedFile>>(
        key,
        (oldData) => {
          const result = insertAtBeginningOfPage(
            { data: data.created, count: data.created.length, has_more: false },
            oldData,
          )
          return result
        },
      )

      const filesAwaitingIndex = data.created.filter(
        (file) =>
          file.index_status !== 'failed' && file.index_status !== 'aborted',
      )
      markFilesIndexStartedInCache({
        queryClient,
        fileIds: filesAwaitingIndex.map((file) => file.id),
        orgId,
      })
      queryClient.invalidateQueries({
        queryKey: organizationFilesIndexStatusQueryKeys.all(),
      })

      if (data.failed.length > 0) {
        toast.error(
          `${data.failed.length} of ${data.created.length + data.failed.length} files could not be fully processed. Successful uploads were kept.`,
        )
      }
    },
  })
}
